import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// 扩展dayjs支持时区和UTC
dayjs.extend(utc);
dayjs.extend(timezone);

// 检查插件是否正确加载
if (!dayjs.utc) {
  console.error('❌ dayjs UTC插件加载失败');
}
if (!dayjs.tz) {
  console.error('❌ dayjs timezone插件加载失败');
}

// 类型定义
export type DayjsType = Dayjs;

/**
 * 时区管理工具
 */

// 可用的时区列表，按地区分组
export const AVAILABLE_TIMEZONES = {
  '中国': ['Asia/Shanghai', 'Asia/Urumqi'],
  '东亚': ['Asia/Tokyo', 'Asia/Seoul', 'Asia/Singapore', 'Asia/Hong_Kong'],
  '北美': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'],
  '欧洲': ['Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome'],
  '大洋洲': ['Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland'],
  '其他': ['UTC', 'GMT']
};

// 默认时区
export const DEFAULT_TIMEZONE = 'Asia/Shanghai';

/**
 * 获取用户时区
 * @param userTimezone 用户设置的时区
 * @returns 用户时区或默认时区
 */
export function getUserTimezone(userTimezone?: string): string {
  return userTimezone || DEFAULT_TIMEZONE;
}

/**
 * 将UTC时间转换为用户时区
 * @param utcTime UTC时间字符串或Date对象
 * @param userTimezone 用户时区
 * @returns 用户时区的dayjs对象
 */
export function convertToUserTimezone(utcTime: string | Date, userTimezone: string): Dayjs {
  const timezone = getUserTimezone(userTimezone);
  return dayjs.utc(utcTime).tz(timezone);
}

/**
 * 将用户时区时间转换为UTC
 * @param localTime 本地时间字符串或Date对象
 * @param userTimezone 用户时区
 * @returns UTC的dayjs对象
 */
export function convertToUTC(localTime: string | Date, userTimezone: string): Dayjs {
  const timezone = getUserTimezone(userTimezone);
  return dayjs.tz(localTime, timezone).utc();
}

/**
 * 格式化时间显示
 * @param time 时间（UTC或本地时间）
 * @param userTimezone 用户时区
 * @param format 格式化字符串
 * @returns 格式化后的时间字符串
 */
export function formatTimeForUser(
  time: string | Date, 
  userTimezone: string, 
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string {
  const userTime = convertToUserTimezone(time, userTimezone);
  return userTime.format(format);
}

/**
 * 获取当前用户时区的时间
 * @param userTimezone 用户时区
 * @returns 用户时区的当前时间
 */
export function getCurrentUserTime(userTimezone: string): Dayjs {
  const timezone = getUserTimezone(userTimezone);
  return dayjs().tz(timezone);
}

/**
 * 验证时区是否有效
 * @param timezoneStr 时区字符串
 * @returns 是否有效
 */
export function isValidTimezone(timezoneStr: string): boolean {
  if (!timezoneStr || typeof timezoneStr !== 'string') {
    return false;
  }
  
  try {
    // 检查dayjs插件是否正确加载
    if (!dayjs.tz) {
      console.error('❌ dayjs timezone插件未加载');
      return false;
    }
    
    // 尝试使用时区
    const testTime = dayjs().tz(timezoneStr);
    return testTime.isValid();
  } catch (error) {
    console.error(`❌ 时区验证失败 ${timezoneStr}:`, error);
    return false;
  }
}

/**
 * 获取时区偏移量
 * @param timezone 时区
 * @returns 偏移量字符串（如 +08:00）
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const offset = dayjs().tz(timezone).format('Z');
    return offset;
  } catch {
    return '+00:00';
  }
}

/**
 * 获取时区显示名称
 * @param timezone 时区
 * @returns 显示名称
 */
export function getTimezoneDisplayName(timezone: string): string {
  const offset = getTimezoneOffset(timezone);
  const timezoneParts = timezone.split('/');
  const city = timezoneParts[timezoneParts.length - 1]?.replace('_', ' ') || timezone;
  return `${city} (${offset})`;
}

/**
 * 创建时区选择器选项
 * @returns 时区选择器选项数组
 */
export function createTimezoneOptions() {
  const options: { label: string; value: string }[] = [];
  
  Object.entries(AVAILABLE_TIMEZONES).forEach(([region, timezones]) => {
    // 为每个时区创建选项
    timezones.forEach(tz => {
      options.push({
        label: `${region} - ${getTimezoneDisplayName(tz)}`,
        value: tz
      });
    });
  });
  
  return options;
}

/**
 * 时区相关的类型定义
 */
export interface TimezoneInfo {
  timezone: string;
  displayName: string;
  offset: string;
  currentTime: string;
}

/**
 * 获取时区信息
 * @param timezone 时区字符串
 * @returns 时区信息对象
 */
export function getTimezoneInfo(timezone: string): TimezoneInfo {
  const displayName = getTimezoneDisplayName(timezone);
  const offset = getTimezoneOffset(timezone);
  const currentTime = getCurrentUserTime(timezone).format('YYYY-MM-DD HH:mm:ss');
  
  return {
    timezone,
    displayName,
    offset,
    currentTime
  };
}

/**
 * 调试函数：打印时区选项信息
 */
export function debugTimezoneOptions() {
  console.log('🔍 调试时区选项:');
  console.log('可用的时区列表:', AVAILABLE_TIMEZONES);
  
  const options = createTimezoneOptions();
  console.log('创建的时区选项:', options);
  
  // 测试几个时区的验证
  const testTimezones = ['Asia/Shanghai', 'America/New_York', 'Europe/London', 'UTC'];
  testTimezones.forEach(tz => {
    console.log(`时区 ${tz} 是否有效:`, isValidTimezone(tz));
  });
  
  return options;
}
